import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Car, Bike, Navigation, MapPin, Star, BadgeCheck, Loader2, RefreshCw, Snowflake } from "lucide-react";
import { Button } from "../../components/ui/button";
import { PageHeader, EmptyState, LoadingBlock } from "../../components/app/ui";
import { customerApi } from "../../lib/api";
import { useLocationCtx } from "../../context/LocationContext";

const VEHICLE_LABEL = (c, t) => {
  const v = (c || t || "").toLowerCase();
  if (v.includes("private_car") || v.includes("car")) return "Car";
  if (v.includes("jumbo")) return "Jumbo taxi";
  if (v.includes("auto")) return "Autorickshaw";
  if (v.includes("scooter") || v.includes("bike")) return "Scooter";
  return "Vehicle";
};

export default function DriversNearMe() {
  const { location, openPrompt } = useLocationCtx();
  const [drivers, setDrivers] = useState(null);

  const load = () => {
    if (!location?.lat) { setDrivers([]); return; }
    setDrivers(null);
    customerApi.nearbyDrivers(location.lat, location.lng).then(setDrivers).catch(() => setDrivers([]));
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    load();
    if (!location?.lat) return undefined;
    const t = setInterval(load, 10000);  // keep the count live as drivers come online / go offline
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location?.lat, location?.lng]);

  return (
    <>
      <PageHeader
        title="Drivers near me"
        description="Verified driver partners currently online around your location."
        actions={<Button variant="outline" size="sm" className="rounded-full" onClick={load} data-testid="drivers-refresh"><RefreshCw className="mr-2 h-4 w-4" /> Refresh</Button>}
      />
      <div className="mb-4 flex flex-wrap items-center gap-2 rounded-2xl border border-surface-300 bg-white/70 px-4 py-2.5 text-sm" data-testid="drivers-location-bar">
        <MapPin className="h-4 w-4 text-brand" />
        <span className="text-ink-soft">{location?.label ? <>Around <strong className="text-ink">{location.label}</strong></> : "Set your location to see nearby drivers"}</span>
        <Button type="button" variant="outline" size="sm" onClick={openPrompt} className="ml-auto rounded-full" data-testid="drivers-change-location">
          <Navigation className="mr-1.5 h-3.5 w-3.5" /> Change
        </Button>
      </div>

      {!location?.lat ? (
        <EmptyState icon={MapPin} title="No location set" text="Set your location to find drivers nearby." />
      ) : drivers === null ? (
        <LoadingBlock rows={3} />
      ) : drivers.length === 0 ? (
        <EmptyState icon={Car} title="No drivers online nearby" text="No verified partners are online around you right now. Try again shortly." />
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3" data-testid="drivers-grid">
          {drivers.map((d) => {
            const isCar = (d.vehicle_category || d.vehicle_type || "").toLowerCase().includes("car") || (d.vehicle_category || "").includes("jumbo");
            const Icon = isCar ? Car : Bike;
            return (
              <div key={d.driver_id} className="kt-card flex items-center gap-3 p-4" data-testid={`driver-card-${d.driver_id}`}>
                <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-brand-50 text-brand"><Icon className="h-6 w-6" /></span>
                <div className="min-w-0 flex-1">
                  <p className="flex items-center gap-1.5 truncate text-sm font-semibold text-ink">
                    {d.name || "Driver partner"}
                    {d.verified && <BadgeCheck className="h-4 w-4 shrink-0 text-emerald-600" data-testid={`driver-verified-${d.driver_id}`} />}
                  </p>
                  <p className="text-xs text-ink-soft">{VEHICLE_LABEL(d.vehicle_category, d.vehicle_type)}</p>
                  {d.has_ac ? <span className="mt-1 inline-flex w-fit items-center gap-1 rounded-full bg-sky-50 px-2 py-0.5 text-[10px] font-bold text-sky-700" data-testid={`driver-ac-${d.driver_id}`}><Snowflake className="h-3 w-3" /> AC</span> : null}
                  <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-ink-muted">
                    <span className="inline-flex items-center gap-1 font-medium text-ink-soft" data-testid={`driver-distance-${d.driver_id}`}><MapPin className="h-3.5 w-3.5" />{d.distance_km} km</span>
                    <span className="inline-flex items-center gap-1"><Navigation className="h-3.5 w-3.5" />~{d.eta_min} min</span>
                    {d.rating && <span className="inline-flex items-center gap-1 font-semibold text-amber-700"><Star className="h-3.5 w-3.5 fill-amber-500 text-amber-500" />{d.rating}</span>}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
      <p className="mt-6 text-center text-xs text-ink-muted">Looking to book? Head to <Link to="/app/home" className="font-semibold text-brand hover:underline">Book a ride</Link> or <Link to="/app/private-cars" className="font-semibold text-brand hover:underline">Private cars</Link>.</p>
    </>
  );
}
